import {notification} from 'antd';
import {InterceptorRequestHandler, InterceptorResponseHandler, AccAxiosRequestConfig} from './types';
import {noFormatList} from './index';

/**
   * 字符串下划线转驼峰
   * @param {String} value 需要转换的值
   */
const formatToHump = (value: string) => {
    return value.replace(/\_(\w)/g, (_, letter) => letter.toUpperCase());
};

/**
   * 字符串驼峰转下划线
   * @param {String} value
   */
const formatToLine = (value: string) => {
    return value.replace(/([A-Z])/g, '_$1').toLowerCase();
};

/**
 * 数据对象key 驼峰下划线相互转化
 * @param {Object} data - 原始对象 支持-数组、key-value对象、字符串
 * @param {String} type hump-转驼峰 toLine-转下划线
 */
export const formatHumpLineTransfer = (data: any, type = 'hump') => {
    let hump = '';
    // 转换对象中的每一个键值为驼峰的递归
    let formatTransferKey = (data: any) => {
        if (data instanceof Array) {
            data.forEach(item => formatTransferKey(item));
        }
        else if (data instanceof Object) {
            for (let key in data) {
                if (data.hasOwnProperty(key)) {
                    hump = type === 'hump' ? formatToHump(key) : formatToLine(key);
                    data[hump] = data[key];
                    if (key !== hump) {
                        delete data[key];
                    }
                    if (data[hump] instanceof Object) {
                        formatTransferKey(data[hump]);
                    }
                }
            }
        }
        else if (typeof data === 'string') {
            data = type === 'hump' ? formatToHump(data) : formatToLine(data);
        }
    };

    formatTransferKey(data);

    return data;
};

// HTTP CODE 对照码
// MSDN: https://developer.mozilla.org/zh-CN/docs/Web/HTTP/Status
const HTTP_CODE_HASH: Record<number, string> = {
    0: '成功',
    100: '传入参数不正确',
    101: '数据已存在',
    102: 'JWT token生成错误',
    400: '错误请求',
    401: '权限校验失败',
    403: '没有该中心的访问权限，如有需要请与管理员联系',
    404: '未找到相关数据',
    405: '请求方法未允许',
    408: '请求超时',
    500: '服务器错误',
    502: '网关错误',
    503: '服务不可用',
    999: '网络错误，请稍后重试',
    1001: '请求超时，请稍后重试',
    200101: '无权限访问',
    200102: '登录状态过期',
};

export const hintNetError = (code: number, url: string, extraMsg: string) => {
    notification.error({
        message: `${HTTP_CODE_HASH[code] || HTTP_CODE_HASH[999]}`,
        description: (
            <div>
                {url && <p>请求的URL: {url}</p>}
                <p>其他信息: {extraMsg}</p>
            </div>
        ),
        duration: 5,
    });

};

// 请求拦截
export const tokenRequestHandler: InterceptorRequestHandler = {
    // 正常请求向 header 中添加 token (除登录或者退出登录之外的请求）
    onFulfilled: config => {
        config.data = formatHumpLineTransfer(config.data, 'toLine');
        config.params = formatHumpLineTransfer(config.params, 'toLine');

        return config;
    },
    // 异常请求直接拒绝，由响应拦截器统一处理
    onRejected: Promise.reject,
};

// 响应拦截 - 处理异常
export const commonErrorHandler: InterceptorResponseHandler = {
    // 业务异常响应由业务代码处理
    onFulfilled: response => {
        if (response && response.data && response.data.errno === 401) {
            hintNetError(401, '', '权限校验失败');
        }

        // 检查是否需要跳过格式转换
        const config = response.config as AccAxiosRequestConfig;
        if (config?.skipResponseFormat) {
            return Promise.resolve(response);
        }

        return Promise.resolve(formatHumpLineTransfer(response, 'hump'));
    },
    // 拦截网络异常响应，进行提示
    onRejected: err => {
        const {response, message, config} = err || {};
        const statusCode = +response?.status;
        const url = config?.url;
        const {msg, errmsg} = response?.data || {};
        const serverRespondMessage = msg || errmsg || String(response?.data);
        // 超时提示
        if (message?.includes('timeout')) {
            hintNetError(1001, url, '服务器超过2min未响应');
            return Promise.reject('服务器超过2min未响应');
        }

        hintNetError(statusCode, url, serverRespondMessage);

        return Promise.reject(response);
    },
};
